import React, { useState, useRef, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { SUPPORTED_LOCALES, type Locale } from '../i18n/config';
import { switchLocalePath } from '../i18n/routes';
import { useLocale } from '../context/LocaleContext';

interface LanguageSwitcherProps {
  onNavigate?: () => void;
}

const LOCALE_META: Record<string, { flag: string; label: string }> = {
  en: { flag: '🇬🇧', label: 'English' },
  it: { flag: '🇮🇹', label: 'Italiano' },
  fr: { flag: '🇫🇷', label: 'Français' },
  de: { flag: '🇩🇪', label: 'Deutsch' },
  es: { flag: '🇪🇸', label: 'Español' },
  pt: { flag: '🇵🇹', label: 'Português' },
};

const LanguageSwitcher: React.FC<LanguageSwitcherProps> = ({ onNavigate }) => {
  const { locale } = useLocale();
  const navigate = useNavigate();
  const location = useLocation();
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  const handleSwitch = (targetLocale: Locale) => {
    if (targetLocale === locale) {
      setOpen(false);
      return;
    }
    const newPath = switchLocalePath(location.pathname, targetLocale);
    navigate(newPath);
    setOpen(false);
    onNavigate?.();
  };

  // Close on outside click
  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const current = LOCALE_META[locale] || { flag: '🌐', label: locale };

  return (
    <div ref={ref} className="relative">
      <button
        onClick={() => setOpen(!open)}
        className="flex items-center gap-1.5 text-sm font-medium px-2.5 py-1.5 rounded-lg transition-colors hover:bg-black/5 dark:hover:bg-white/10"
        aria-label="Change language"
        aria-expanded={open}
      >
        <span className="text-base leading-none">{current.flag}</span>
        <span className="uppercase tracking-wide">{locale}</span>
        <svg
          className={`w-3 h-3 transition-transform ${open ? 'rotate-180' : ''}`}
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
        </svg>
      </button>

      {open && (
        <div className="absolute top-full right-0 mt-1 bg-white dark:bg-brand-charcoal rounded-lg shadow-lg border border-black/10 dark:border-white/10 overflow-hidden min-w-[160px] z-50">
          {SUPPORTED_LOCALES.map((l) => {
            const meta = LOCALE_META[l] || { flag: '🌐', label: l };
            const isActive = l === locale;
            return (
              <button
                key={l}
                onClick={() => handleSwitch(l)}
                className={`w-full flex items-center gap-3 px-4 py-2.5 text-sm transition-colors text-left
                  ${isActive
                    ? 'bg-brand-blue/10 text-brand-blue font-medium'
                    : 'hover:bg-black/5 dark:hover:bg-white/10 text-black dark:text-white'
                  }`}
              >
                <span className="text-lg leading-none">{meta.flag}</span>
                <span>{meta.label}</span>
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default LanguageSwitcher;
